import { twClassMerge } from '@healthhub/react-ui';
import { Skeleton, Typography } from 'antd';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FilledAddIcon, FilledCloseIcon } from '@shared/assets-icon';
import { Button, MaxWidthContainer } from '@shared/components';
import { PortalSearchInput } from '@shared/components/V2';
import useResponsive from '@shared/hooks/useResponsive';

import LookForEServicesActiveSection from './LookForEServicesActiveSection';
import LookForEServicesSearchEmptyResults from './LookForEServicesSearchEmptyResults';
import LookForEServicesSearchResults from './LookForEServicesSearchResults';
import type { IEservices, IEservicesGroupModule } from '../types/eservices';
import { getFilterEServicesModules } from '../utils';

const { Text, Title } = Typography;

interface LookForEServicesProps {
  eservices: IEservices[];
  isFetching: boolean;
}

const LookForEServices = ({ eservices, isFetching }: LookForEServicesProps) => {
  const { t } = useTranslation('home');
  const responsive = useResponsive();
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [currentTab, setCurrentTab] = useState<string | undefined>();

  const allModules = useMemo(
    () =>
      eservices.reduce<IEservicesGroupModule[]>((accumulator, currentValue) => {
        currentValue.groups.forEach(({ modules }) => {
          accumulator.push(...modules);
        });

        return accumulator;
      }, []),
    [eservices]
  );

  const filterModules = useMemo(
    () => getFilterEServicesModules({ allModules, filterValue: searchInputValue }),
    [searchInputValue, allModules]
  );

  const toggleEService = (label: string) => {
    setCurrentTab((prev) => {
      if (prev === label) {
        return undefined;
      }
      return label;
    });
  };

  const onClickEService = (e: MouseEvent<HTMLDivElement>, label: string) => {
    e.stopPropagation();
    toggleEService(label);
  };

  const onKeyDownEService = (e: KeyboardEvent<HTMLDivElement>, label: string) => {
    e.stopPropagation();
    if (e.key === 'Enter' || e.key === ' ') {
      toggleEService(label);
    }
  };

  const renderActiveSectionDisplay = () => {
    const activeSection = eservices.find(({ label }) => label === currentTab);

    return (
      <div className="flex flex-col gap-4 lg:gap-10">
        {activeSection?.extra}
        {activeSection?.groups.map(({ description, label, modules }, index) => (
          <LookForEServicesActiveSection
            description={description}
            key={label}
            label={label}
            modules={modules}
            showTitle={index !== 0 || activeSection?.groups.length > 1 || responsive.lg}
          />
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (responsive.md && eservices?.[0]?.label) {
      setCurrentTab(eservices?.[0]?.label);
    }
  }, [eservices, responsive.md]);

  return (
    <div className="flex w-full justify-center bg-functional-haze pb-10 lg:px-18 lg:py-15">
      <MaxWidthContainer>
        <div className="flex flex-col lg:gap-10">
          <div className="flex flex-col">
            <div className="flex flex-col gap-4 px-3 py-6 md:px-15">
              <Title className="m-0 w-60 self-center text-center text-3xl text-primary-blue-grey-4 md:w-full">
                {t('lookingForAnEService')}
              </Title>
              <PortalSearchInput
                className="self-center"
                onChange={(e) => setSearchInputValue(e.target.value)}
                placeholder={t('whatYouAreLooking')}
                value={searchInputValue}
              />
            </div>
            {isFetching ? (
              <Skeleton.Button active className="w-full" />
            ) : (
              <>
                {!!searchInputValue && filterModules.length > 0 && (
                  <LookForEServicesSearchResults filterModules={filterModules} searchInputValue={searchInputValue} />
                )}
                {!searchInputValue && (
                  <div className="flex flex-col lg:gap-4">
                    <Text className="px-3 py-2 text-base text-primary-blue-grey md:px-8 lg:p-0">
                      {t('exploreOurServices')}
                    </Text>
                    <div className="hidden flex-wrap gap-4 lg:flex">
                      {eservices.map(({ label }) => (
                        <Button
                          className={twClassMerge(
                            'font-medium px-10 py-0 h-12 text-lg',
                            currentTab === label
                              ? 'bg-primary-blue-grey-3'
                              : 'bg-white border-primary-green text-primary-green-4 border-2'
                          )}
                          key={label}
                          lang="en"
                          onClick={() => setCurrentTab(label)}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                    <div className="flex flex-col bg-white lg:hidden">
                      {eservices.map(({ description, label }) => (
                        <div
                          className="flex flex-col border-b border-b-secondary-green-100 px-6 py-4 text-start"
                          key={label}
                          onClick={(e) => onClickEService(e, label)}
                          onKeyDown={(e) => onKeyDownEService(e, label)}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="flex flex-col gap-6">
                            <div className="ml-2">
                              <div className="flex justify-between">
                                <Title className="m-0 text-xl text-primary-green-4" lang="en" level={5}>
                                  {label}
                                </Title>
                                {currentTab === label ? (
                                  <FilledCloseIcon className="size-5" />
                                ) : (
                                  <FilledAddIcon className="size-5" />
                                )}
                              </div>
                              <Text className="text-xs" lang="en">
                                {description}
                              </Text>
                            </div>
                            {currentTab === label && renderActiveSectionDisplay()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          {isFetching ? (
            <Skeleton.Button active className="w-full" />
          ) : (
            <>
              {!!searchInputValue && filterModules.length === 0 && (
                <LookForEServicesSearchEmptyResults allModules={allModules} searchInputValue={searchInputValue} />
              )}
              {!searchInputValue && (
                <div className="hidden flex-col gap-10 bg-white px-3 pb-10 pt-4 lg:flex lg:bg-transparent lg:p-0">
                  {renderActiveSectionDisplay()}
                </div>
              )}
            </>
          )}
        </div>
      </MaxWidthContainer>
    </div>
  );
};

export default LookForEServices;
